import { CanMatchFn, Routes } from '@angular/router';
import { autoLoginPartialRoutesGuard } from 'angular-auth-oidc-client';
import { PANORAMA_SE } from './app.constants';
import { APP_ROUTE_PATHS } from './app.routes.constant';
import { climateCortexModelLoadGuard } from './feature/editor/services/climate-cortex-model-load.guard';
import { UI_STRINGS } from './functions/language/uistrings-tolgee';
import { editorGuard } from './guards/editor.guard';
import { publicVersionComponentResolver, publicVersionMatcher } from './guards/public-version-matcher.guard';
import { CustomDomainDashboardData } from './model/custom-domain-dashboard-data';
import { featureFlagsResolver } from './resolvers/feature-flags.resolver';
import { SelfContainedDashboardResolverV3 } from './standalone/components/self-contained-dashboard-v3/self-contained-dashboard-v3.resolver';
import { v4Resolver } from './standalone/components/v4-self-contained-dashboard/v4.resolver';

const autoLoginPartialRoutesGuardLogStart: CanMatchFn = (_, segments) => {
  console.debug('-- autoLoginPartialRoutesGuard start -- \nPath: ', segments.map((s) => s.path).join('/'));
  return true;
};
const autoLoginPartialRoutesGuardLogEnd: CanMatchFn = () => {
  console.debug('-- autoLoginPartialRoutesGuard success --');
  return true;
};
const autoLoginPartialRoutesGuards = [
  autoLoginPartialRoutesGuardLogStart,
  autoLoginPartialRoutesGuard,
  autoLoginPartialRoutesGuardLogEnd,
];

const publicRoutes: Routes = [
  {
    /**
     * This route is used to redirect to the correct version of the dashboard.
     */
    canActivate: [publicVersionMatcher],
    path: `${APP_ROUTE_PATHS.PUBLIC.ROOT}/${APP_ROUTE_PATHS.PUBLIC.BOARD}/:boardId`,
    data: { reason: UI_STRINGS.NOT_FOUND.DASHBOARD_NOT_FOUND, isPublicDashboard: true },
    resolve: { componentData: publicVersionComponentResolver, featureFlagsLoaded: featureFlagsResolver },
    loadComponent: () =>
      import('./standalone/components/dynamic-dashboard-versioner/dynamic-dashboard-versioner.component').then(
        (m) => m.DynamicDashboardVersionerComponent
      ),
  },
  {
    path: APP_ROUTE_PATHS.PUBLIC.V3,
    children: [
      {
        canActivate: [publicVersionMatcher],
        path: `${APP_ROUTE_PATHS.PUBLIC.BOARD}/:boardId`,
        loadComponent: () =>
          import('./standalone/components/self-contained-dashboard-v3/self-contained-dashboard.component').then(
            (m) => m.SelfContainedDashboardComponent
          ),
        resolve: { dashBoard: SelfContainedDashboardResolverV3, featureFlagsLoaded: featureFlagsResolver },
        data: { isPublicDashboard: true },
      },
    ],
  },
  {
    path: APP_ROUTE_PATHS.PUBLIC.V4,
    children: [
      {
        canActivate: [publicVersionMatcher],
        path: `${APP_ROUTE_PATHS.PUBLIC.BOARD}/:boardId`,
        loadComponent: () =>
          import('./standalone/components/v4-self-contained-dashboard/v4-self-contained-dashboard.component').then(
            (m) => m.V4SelfContainedDashboardComponent
          ),
        resolve: { dashBoard: v4Resolver },
        data: { isPublicDashboard: true },
      },
    ],
  },
  {
    path: APP_ROUTE_PATHS.SWEDEN,
    redirectTo: `${APP_ROUTE_PATHS.PUBLIC.ROOT}/${APP_ROUTE_PATHS.PUBLIC.BOARD}/${PANORAMA_SE}`,
    pathMatch: 'full',
  },
];

export const fullAppRoutes: Routes = [
  ...publicRoutes,
  /**
   * Cookie notice route
   */
  {
    path: APP_ROUTE_PATHS.COOKIE_NOTICE,
    loadComponent: () =>
      import('./components/cookie-notice/cookie-notice-page/cookie-notice-page.component').then(
        (m) => m.CookieNoticePageComponent
      ),
  },

  {
    path: APP_ROUTE_PATHS.EDITOR.SIGN_IN,
    loadComponent: () => import('./feature/sign-in/sign-in.component').then((m) => m.SignInComponent),
  },
  /**
   * Editor routes
   */
  {
    path: APP_ROUTE_PATHS.EDITOR.ROOT,
    canMatch: [...autoLoginPartialRoutesGuards, editorGuard],
    children: [
      {
        path: APP_ROUTE_PATHS.EDITOR.EDITOR.ROOT,
        loadChildren: () => import('./feature/editor/editor.module').then((m) => m.EditorModule),
        canMatch: [...autoLoginPartialRoutesGuards, climateCortexModelLoadGuard],
        resolve: { featureFlagsLoaded: featureFlagsResolver },
      },
      {
        path: APP_ROUTE_PATHS.EDITOR.NO_MEMBERSHIP,
        loadComponent: () =>
          import('./feature/no-membership/no-membership.component').then((m) => m.NoMembershipComponent),
      },
      {
        path: APP_ROUTE_PATHS.EDITOR.ADMIN.ROOT,
        loadChildren: () => import('./feature/admin/admin.module').then((m) => m.AdminModule),
        canMatch: [...autoLoginPartialRoutesGuards, climateCortexModelLoadGuard],
      },
    ],
    runGuardsAndResolvers: 'always',
  },
  /**
   * Routes not found
   */
  {
    path: APP_ROUTE_PATHS.NOT_FOUND,
    loadComponent: () => import('./feature/not-found/not-found.component').then((m) => m.NotFoundComponent),
  },
];

/**
 * Customised routes for public dashboards on custom domains
 */
export const dashboardAppRoutes: ({ boardId, publishedVersion }: CustomDomainDashboardData) => Routes = ({
  boardId,
  publishedVersion,
}) => {
  switch (publishedVersion) {
    case 'v3':
    case 'v3.1':
    case 'v3.2':
      return [
        {
          path: '',
          loadComponent: () =>
            import('./standalone/components/self-contained-dashboard-v3/self-contained-dashboard.component').then(
              (m) => m.SelfContainedDashboardComponent
            ),
          resolve: { dashBoard: SelfContainedDashboardResolverV3, featureFlagsLoaded: featureFlagsResolver },
          data: { boardId, publishedVersion, isPublicDashboard: true },
        },
        {
          path: APP_ROUTE_PATHS.COOKIE_NOTICE,
          loadComponent: () =>
            import('./components/cookie-notice/cookie-notice-page/cookie-notice-page.component').then(
              (m) => m.CookieNoticePageComponent
            ),
        },
      ];

    case 'v4':
    case 'v4.1':
    case 'v4.2':
      return [
        {
          path: '',
          loadComponent: () =>
            import('./standalone/components/v4-self-contained-dashboard/v4-self-contained-dashboard.component').then(
              (m) => m.V4SelfContainedDashboardComponent
            ),
          resolve: { dashBoard: v4Resolver },
          data: { boardId, isPublicDashboard: true },
        },
        {
          path: APP_ROUTE_PATHS.COOKIE_NOTICE,
          loadComponent: () =>
            import('./components/cookie-notice/cookie-notice-page/cookie-notice-page.component').then(
              (m) => m.CookieNoticePageComponent
            ),
        },
      ];
    case 'v5':
    case 'v5.1':
      return [
        {
          path: '',
          loadComponent: () =>
            import('./standalone/components/dynamic-dashboard-versioner/dynamic-dashboard-versioner.component').then(
              (m) => m.DynamicDashboardVersionerComponent
            ),
          resolve: { componentData: publicVersionComponentResolver, featureFlagsLoaded: featureFlagsResolver },
          data: { boardId, isPublicDashboard: true },
        },
        {
          path: APP_ROUTE_PATHS.COOKIE_NOTICE,
          loadComponent: () =>
            import('./components/cookie-notice/cookie-notice-page/cookie-notice-page.component').then(
              (m) => m.CookieNoticePageComponent
            ),
        },
      ];
    case 'notPublished':
    default:
      return [
        {
          path: '',
          loadComponent: () => import('./feature/not-found/not-found.component').then((m) => m.NotFoundComponent),
          data: { link: 'https://app.climateview.global/', reason: UI_STRINGS.NOT_FOUND.DASHBOARD_NOT_FOUND },
        },
      ];
  }
};
